import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Globe, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddServerModal = ({ open, onClose, onSubmit, groups, editingServer }) => {
  const [name, setName] = useState("");
  const [addresses, setAddresses] = useState([{ id: "", url: "", label: "" }]);
  const [groupId, setGroupId] = useState("none");
  const [notes, setNotes] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingFavicon, setFetchingFavicon] = useState(false);

  useEffect(() => {
    if (open) {
      if (editingServer) {
        setName(editingServer.name || "");
        setAddresses(
          editingServer.addresses?.length > 0
            ? editingServer.addresses
            : [{ id: "", url: "", label: "" }]
        );
        setGroupId(editingServer.group_id || "none");
        setNotes(editingServer.notes || "");
        setFaviconUrl(editingServer.favicon_url || "");
      } else {
        setName("");
        setAddresses([{ id: "", url: "", label: "" }]);
        setGroupId("none");
        setNotes("");
        setFaviconUrl("");
      }
    }
  }, [open, editingServer]);

  const handleAddAddress = () => {
    setAddresses([...addresses, { id: "", url: "", label: "" }]);
  };

  const handleRemoveAddress = (index) => {
    if (addresses.length > 1) {
      setAddresses(addresses.filter((_, i) => i !== index));
    }
  };

  const handleAddressChange = (index, field, value) => {
    const newAddresses = [...addresses];
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    setAddresses(newAddresses);
  };

  const fetchFavicon = async (url) => {
    if (!url) return;
    
    setFetchingFavicon(true);
    try {
      const response = await axios.get(`${API}/favicon`, {
        params: { url },
      });
      if (response.data.favicon_url) {
        setFaviconUrl(response.data.favicon_url);
        toast.success("Icon found!");
      }
    } catch (error) {
      console.error("Failed to fetch favicon:", error);
    }
    setFetchingFavicon(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Server name is required");
      return;
    }

    const validAddresses = addresses.filter((a) => a.url.trim());
    if (validAddresses.length === 0) {
      toast.error("At least one URL is required");
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        name: name.trim(),
        addresses: validAddresses.map((a) => ({
          id: a.id || undefined,
          url: a.url.trim(),
          label: a.label?.trim() || null,
        })),
        group_id: (groupId && groupId !== "none") ? groupId : null,
        notes: notes.trim() || null,
        favicon_url: faviconUrl || null,
      });
    } catch (error) {
      console.error("Submit error:", error);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {editingServer ? "Edit Server" : "Add Server"}
          </DialogTitle>
          <DialogDescription>
            {editingServer
              ? "Update your server details"
              : "Add a new server for quick access"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Server Name */}
          <div className="space-y-2">
            <Label htmlFor="server-name">Name</Label>
            <Input
              id="server-name"
              data-testid="server-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Production Server"
              className="h-11 rounded-xl"
              required
            />
          </div>

          {/* Favicon */}
          <div className="space-y-2">
            <Label>Icon</Label>
            <div className="flex items-center gap-3">
              <div className="favicon-container !w-14 !h-14">
                {faviconUrl ? (
                  <img
                    src={faviconUrl}
                    alt=""
                    className="!w-8 !h-8"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <Globe className="w-6 h-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1">
                <Input
                  data-testid="favicon-url-input"
                  value={faviconUrl}
                  onChange={(e) => setFaviconUrl(e.target.value)}
                  placeholder="Icon URL (optional)"
                  className="h-10 rounded-xl text-sm"
                />
              </div>
              {addresses[0]?.url && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchFavicon(addresses[0].url)}
                  disabled={fetchingFavicon}
                  className="rounded-xl gap-1.5"
                  data-testid="fetch-favicon-btn"
                >
                  {fetchingFavicon ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" />
                      Auto
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Addresses */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>URLs</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddAddress}
                className="h-8 text-xs gap-1"
                data-testid="add-address-btn"
              >
                <Plus className="h-3.5 w-3.5" />
                Add URL
              </Button>
            </div>

            <div className="space-y-3">
              {addresses.map((address, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-1 space-y-2">
                    <Input
                      data-testid={`address-url-input-${index}`}
                      value={address.url}
                      onChange={(e) => handleAddressChange(index, "url", e.target.value)}
                      placeholder="https://example.com"
                      className="h-10 rounded-xl font-mono text-sm"
                    />
                    <Input
                      data-testid={`address-label-input-${index}`}
                      value={address.label || ""}
                      onChange={(e) => handleAddressChange(index, "label", e.target.value)}
                      placeholder="Label (e.g., Admin Panel)"
                      className="h-9 rounded-xl text-sm"
                    />
                  </div>
                  {addresses.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveAddress(index)}
                      className="h-10 w-10 shrink-0 rounded-xl"
                      data-testid={`remove-address-btn-${index}`}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Group */}
          <div className="space-y-2">
            <Label>Group</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger data-testid="group-select" className="h-11 rounded-xl">
                <SelectValue placeholder="Select a group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Group</SelectItem>
                {[...groups].sort((a, b) => a.name.localeCompare(b.name)).map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: group.color }}
                      />
                      {group.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              data-testid="notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this server..."
              rows={2}
              className="rounded-xl resize-none"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="rounded-xl gap-2" data-testid="submit-server-btn">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingServer ? "Save Changes" : "Add Server"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddServerModal;
